import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button, Input, Label } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { MenuCategory, MenuOption, MenuOptionGroup, tableOrderService, } from "@/api/services/table-order.service";
import { SITE_ID } from "@/assets/meta/meta";
import { findMissingRequiredFields, focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";
import { dialogService } from "@/utils/dialogService";

const fulfillmentTypeLabels = {
    INSTANT: "즉시 제공",
    KITCHEN: "제조 필요",
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
    const detail = (error as any)?.response?.data?.errors?.[0]?.detail;
    if (typeof detail === "string" && detail.trim()) {
        return detail;
    }
    if (typeof (error as any)?.message === "string" && (error as any).message.trim()) {
        return (error as any).message;
    }
    return fallbackMessage;
};

const defaultGroupDraft = {
    name: "",
    is_required: false,
    min_select: 0,
    max_select: 1,
    display_order: 0,
    is_active: true,
};
type GroupDraft = typeof defaultGroupDraft;
const MenuForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isCreate = useMemo(() => id === "0", [id]);
    const [categories, setCategories] = useState<MenuCategory[]>([]);
    const [groups, setGroups] = useState<MenuOptionGroup[]>([]);
    const [options, setOptions] = useState<MenuOption[]>([]);
    const [category, setCategory] = useState<string>("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState(0);
    const [displayOrder, setDisplayOrder] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [isKioskVisible, setIsKioskVisible] = useState(true);
    const [isWebVisible, setIsWebVisible] = useState(true);
    const [isPosVisible, setIsPosVisible] = useState(true);
    const [isSoldOut, setIsSoldOut] = useState(false);
    const [fulfillmentType, setFulfillmentType] = useState<"INSTANT" | "KITCHEN">("KITCHEN");
    const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [newGroupDraft, setNewGroupDraft] = useState<GroupDraft>(defaultGroupDraft);
    const [groupEdits, setGroupEdits] = useState<Record<number, GroupDraft>>({});
    const [newOptionDrafts, setNewOptionDrafts] = useState<Record<number, {
        name: string;
        extra_price: number;
      tax_amount: number;
      supply_amount: number;
      tax_free_amount: number;
      tip_amount: number;
        display_order: number;
        is_active: boolean;
    }>>({});
    const [optionEdits, setOptionEdits] = useState<Record<number, {
        name: string;
        extra_price: number;
      tax_amount: number;
      supply_amount: number;
      tax_free_amount: number;
      tip_amount: number;
        display_order: number;
        is_active: boolean;
    }>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const menuId = useMemo(() => (isCreate || !id ? null : Number(id)), [id, isCreate]);
    const optionsByGroup = useMemo(() => {
        const map: Record<number, MenuOption[]> = {};
        options.forEach((option) => {
            if (!map[option.option_group])
                map[option.option_group] = [];
            map[option.option_group].push(option);
        });
        return map;
    }, [options]);
      const displayedImageUrl = imagePreviewUrl || (!imageRemoved ? existingImageUrl : null);
    const loadOptionData = useCallback(async (targetMenuId: number) => {
        const [groupRes, optionRes] = await Promise.all([
            tableOrderService.listAllMenuOptionGroups({ menu_id: targetMenuId }),
            tableOrderService.listAllMenuOptions({ menu_id: targetMenuId }),
        ]);
        setGroups(groupRes.results || []);
        setOptions(optionRes.results || []);
        setGroupEdits({});
        setOptionEdits({});
    }, []);
    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const categoryRes = await tableOrderService.listAllMenuCategories();
            setCategories(categoryRes.results || []);
            if (!menuId)
                return;
            const menuRes = await tableOrderService.getMenu(menuId);
            const menu = menuRes.data;
            setCategory(String(menu.category));
            setName(menu.name || "");
            setDescription(menu.description || "");
            setPrice(menu.price || 0);
            setDisplayOrder(menu.display_order || 0);
            setIsActive(Boolean(menu.is_active));
            setIsKioskVisible(Boolean(menu.is_kiosk_visible));
            setIsWebVisible(Boolean(menu.is_web_visible));
            setIsPosVisible(Boolean(menu.is_pos_visible));
            setIsSoldOut(Boolean(menu.is_sold_out));
            setFulfillmentType(menu.fulfillment_type || "KITCHEN");
            setExistingImageUrl(menu.image || null);
            setImageFile(null);
            setImagePreviewUrl(null);
            setImageRemoved(false);
            await loadOptionData(menuId);
        }
        catch (error) {
            toast.error("메뉴 정보를 불러오지 못했습니다.");
            navigate("/table-order/menus");
        }
        finally {
            setLoading(false);
        }
    }, [loadOptionData, menuId, navigate]);
    useEffect(() => {
        loadInitial();
    }, [loadInitial]);
    useEffect(() => {
      return () => {
        if (imagePreviewUrl) {
          URL.revokeObjectURL(imagePreviewUrl);
        }
      };
    }, [imagePreviewUrl]);
    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFile = event.target.files?.[0] || null;
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      if (!nextFile) {
        setImageFile(null);
        setImagePreviewUrl(null);
        return;
      }
      setImageFile(nextFile);
      setImagePreviewUrl(URL.createObjectURL(nextFile));
      setImageRemoved(false);
    };
    const handleRemoveImage = () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImageFile(null);
      setImagePreviewUrl(null);
      setExistingImageUrl(null);
      setImageRemoved(true);
    };
    const handleSaveMenu = async () => {
        const missingRequiredFields = findMissingRequiredFields([
            { label: "카테고리", value: category, elementId: "menuCategory" },
            { label: "메뉴명", value: name, elementId: "menuName" },
        ]);
        if (missingRequiredFields.length > 0) {
            toast.error(formatRequiredMessage(missingRequiredFields));
            focusFirstMissingField(missingRequiredFields);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...(Number.isFinite(SITE_ID) ? { site: SITE_ID } : {}),
                category: Number(category),
                name: name.trim(),
                description,
                price,
                display_order: displayOrder,
                is_active: isActive,
                is_kiosk_visible: isKioskVisible,
                is_web_visible: isWebVisible,
                is_pos_visible: isPosVisible,
                is_sold_out: isSoldOut,
                fulfillment_type: fulfillmentType,
            };
            if (!menuId) {
                const created = imageFile
                  ? await tableOrderService.createMenuWithImage(payload, imageFile)
                  : await tableOrderService.createMenu(payload);
                toast.success("메뉴가 생성되었습니다.");
                navigate(`/table-order/menu-form/${created.data.id}`);
            }
            else {
                if (imageFile) {
                  await tableOrderService.updateMenuWithImage(menuId, payload, imageFile);
                }
                else {
                  await tableOrderService.updateMenu(menuId, {
                    ...payload,
                    ...(imageRemoved ? { image: null } : {}),
                  });
                }
                toast.success("메뉴가 수정되었습니다.");
                const menuRes = await tableOrderService.getMenu(menuId);
                setExistingImageUrl(menuRes.data.image || null);
                setImageFile(null);
                setImagePreviewUrl(null);
                setImageRemoved(false);
            }
        }
        catch (error) {
            toast.error(getErrorMessage(error, "메뉴 저장에 실패했습니다."));
        }
        finally {
            setSaving(false);
        }
    };
    const handleCreateGroup = async () => {
        if (!menuId) {
            toast.error("메뉴를 먼저 저장해주세요.");
            return;
        }
        if (!newGroupDraft.name.trim()) {
            toast.error("옵션 그룹명을 입력해주세요.");
            return;
        }
        if (newGroupDraft.max_select < newGroupDraft.min_select) {
            toast.error("최대 선택 개수는 최소 선택 개수보다 작을 수 없습니다.");
            return;
        }
        try {
            await tableOrderService.createMenuOptionGroup({
                menu: menuId,
                ...newGroupDraft,
                name: newGroupDraft.name.trim(),
            });
            setNewGroupDraft(defaultGroupDraft);
            await loadOptionData(menuId);
            toast.success("옵션 그룹이 생성되었습니다.");
        }
        catch (error) {
            toast.error("옵션 그룹 생성에 실패했습니다.");
        }
    };
    const getGroupEdit = (group: MenuOptionGroup): GroupDraft => {
        return groupEdits[group.id] || {
            name: group.name,
            is_required: group.is_required,
            min_select: group.min_select,
            max_select: group.max_select,
            display_order: group.display_order,
            is_active: group.is_active,
        };
    };
    const handleSaveGroup = async (group: MenuOptionGroup) => {
        if (!menuId)
            return;
        const draft = getGroupEdit(group);
        if (!draft.name.trim()) {
            toast.error("옵션 그룹명을 입력해주세요.");
            return;
        }
        if (draft.max_select < draft.min_select) {
            toast.error("최대 선택 개수는 최소 선택 개수보다 작을 수 없습니다.");
            return;
        }
        try {
            await tableOrderService.updateMenuOptionGroup(group.id, {
                name: draft.name.trim(),
                is_required: draft.is_required,
                min_select: draft.min_select,
                max_select: draft.max_select,
                display_order: draft.display_order,
                is_active: draft.is_active,
            });
            await loadOptionData(menuId);
            toast.success("옵션 그룹이 수정되었습니다.");
        }
        catch (error) {
            toast.error("옵션 그룹 수정에 실패했습니다.");
        }
    };
    const handleDeleteGroup = async (group: MenuOptionGroup) => {
        if (!menuId)
            return;
        if (!await dialogService.confirm("옵션 그룹을 삭제하시겠습니까?"))
            return;
        try {
            await tableOrderService.deleteMenuOptionGroup(group.id);
            await loadOptionData(menuId);
            toast.success("옵션 그룹이 삭제되었습니다.");
        }
        catch (error) {
            toast.error("옵션 그룹 삭제에 실패했습니다.");
        }
    };
    const getOptionEdit = (option: MenuOption) => {
        return optionEdits[option.id] || {
            name: option.name,
            extra_price: option.extra_price,
            tax_amount: option.tax_amount,
            supply_amount: option.supply_amount,
            tax_free_amount: option.tax_free_amount,
            tip_amount: option.tip_amount,
            display_order: option.display_order,
            is_active: option.is_active,
        };
    };
    const handleCreateOption = async (groupId: number) => {
        if (!menuId)
            return;
        const draft = newOptionDrafts[groupId] || { name: "", extra_price: 0, tax_amount: 0, supply_amount: 0, tax_free_amount: 0, tip_amount: 0, display_order: 0, is_active: true };
        if (!draft.name.trim()) {
            toast.error("옵션명을 입력해주세요.");
            return;
        }
        try {
            await tableOrderService.createMenuOption({
                option_group: groupId,
                name: draft.name.trim(),
                extra_price: draft.extra_price,
                tax_amount: draft.tax_amount,
                supply_amount: draft.supply_amount,
                tax_free_amount: draft.tax_free_amount,
                tip_amount: draft.tip_amount,
                display_order: draft.display_order,
                is_active: draft.is_active,
            });
              setNewOptionDrafts((prev) => ({ ...prev, [groupId]: { name: "", extra_price: 0, tax_amount: 0, supply_amount: 0, tax_free_amount: 0, tip_amount: 0, display_order: 0, is_active: true } }));
            await loadOptionData(menuId);
            toast.success("옵션이 생성되었습니다.");
        }
        catch (error) {
            toast.error("옵션 생성에 실패했습니다.");
        }
    };
    const handleSaveOption = async (option: MenuOption) => {
        if (!menuId)
            return;
        const draft = getOptionEdit(option);
        if (!draft.name.trim()) {
            toast.error("옵션명을 입력해주세요.");
            return;
        }
        try {
            await tableOrderService.updateMenuOption(option.id, {
                name: draft.name.trim(),
                extra_price: draft.extra_price,
                tax_amount: draft.tax_amount,
                supply_amount: draft.supply_amount,
                tax_free_amount: draft.tax_free_amount,
                tip_amount: draft.tip_amount,
                display_order: draft.display_order,
                is_active: draft.is_active,
            });
            await loadOptionData(menuId);
            toast.success("옵션이 수정되었습니다.");
        }
        catch (error) {
            toast.error("옵션 수정에 실패했습니다.");
        }
    };
    const handleDeleteOption = async (option: MenuOption) => {
        if (!menuId)
            return;
        if (!await dialogService.confirm("옵션을 삭제하시겠습니까?"))
            return;
        try {
            await tableOrderService.deleteMenuOption(option.id);
            await loadOptionData(menuId);
            toast.success("옵션이 삭제되었습니다.");
        }
        catch (error) {
            toast.error("옵션 삭제에 실패했습니다.");
        }
    };
    return (<div className="page-content">
      <Container fluid>
        <BreadCrumb title={isCreate ? "메뉴 생성" : "메뉴 수정"} pageTitle="현장주문"/>

        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">{isCreate ? "메뉴 생성" : `메뉴 수정 #${id}`}</h5>
            <div className="d-flex gap-2">
              <Button color="light" onClick={() => navigate("/table-order/menus")}>목록</Button>
              <Button color="primary" onClick={handleSaveMenu} disabled={saving || loading}>
                {saving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (<p className="text-muted mb-0">불러오는 중...</p>) : (<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="form-label">카테고리</Label>
                  <select id="menuCategory" className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">선택</option>
                    {categories.map((item) => (<option key={item.id} value={item.id}>{item.name}</option>))}
                  </select>
                </div>
                <div>
                  <Label className="form-label">메뉴명</Label>
                  <Input id="menuName" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}/>
                </div>
                <div>
                  <Label className="form-label">가격</Label>
                  <Input type="number" value={price} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(Number(e.target.value || 0))}/>
                </div>
                <div className="md:col-span-3">
                  <Label className="form-label">설명</Label>
                  <Input value={description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}/>
                </div>
                <div>
                  <Label className="form-label">정렬 순서</Label>
                  <Input type="number" value={displayOrder} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayOrder(Number(e.target.value || 0))}/>
                </div>
                <div>
                  <Label className="form-label">활성 여부</Label>
                  <select className="form-select" value={isActive ? "true" : "false"} onChange={(e) => setIsActive(e.target.value === "true")}>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">웹 노출</Label>
                  <select className="form-select" value={isWebVisible ? "true" : "false"} onChange={(e) => setIsWebVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">키오스크 노출</Label>
                  <select className="form-select" value={isKioskVisible ? "true" : "false"} onChange={(e) => setIsKioskVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">POS 노출</Label>
                  <select className="form-select" value={isPosVisible ? "true" : "false"} onChange={(e) => setIsPosVisible(e.target.value === "true")}>
                    <option value="true">노출</option>
                    <option value="false">숨김</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">판매 상태</Label>
                  <select className="form-select" value={isSoldOut ? "true" : "false"} onChange={(e) => setIsSoldOut(e.target.value === "true")}>
                    <option value="false">판매중</option>
                    <option value="true">품절</option>
                  </select>
                </div>
                <div>
                  <Label className="form-label">제공 타입</Label>
                  <select
                    className="form-select"
                    value={fulfillmentType}
                    onChange={(e) => setFulfillmentType(e.target.value as "INSTANT" | "KITCHEN")}
                  >
                    {Object.entries(fulfillmentTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <Label className="form-label">메뉴 이미지</Label>
                  <div className="border rounded p-3 bg-light">
                    <input type="file" accept="image/*" className="form-control" onChange={handleImageChange}/>
                    {displayedImageUrl ? (<div className="mt-3 d-flex align-items-start gap-3">
                        <img src={displayedImageUrl} alt="메뉴 이미지 미리보기" className="rounded border" style={{ width: 120, height: 120, objectFit: "cover" }}/>
                        <div>
                          <p className="text-muted mb-2">현재 대표 이미지입니다.</p>
                          <Button type="button" color="soft-danger" size="sm" onClick={handleRemoveImage}>이미지 제거</Button>
                        </div>
                      </div>) : (<p className="text-muted small mt-2 mb-0">등록된 이미지가 없습니다.</p>)}
                  </div>
                </div>
              </div>)}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h5 className="mb-0">옵션 그룹/옵션 관리</h5>
          </CardHeader>
          <CardBody>
            {!menuId ? (<p className="text-muted mb-0">메뉴를 먼저 저장하면 옵션 그룹과 옵션을 관리할 수 있습니다.</p>) : (<>
                <div className="border rounded p-3 mb-4">
                  <h6 className="mb-3">옵션 그룹 추가</h6>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                    <Input placeholder="그룹명" value={newGroupDraft.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGroupDraft((prev) => ({ ...prev, name: e.target.value }))}/>
                    <Input type="number" placeholder="최소" value={newGroupDraft.min_select} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGroupDraft((prev) => ({ ...prev, min_select: Number(e.target.value || 0) }))}/>
                    <Input type="number" placeholder="최대" value={newGroupDraft.max_select} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGroupDraft((prev) => ({ ...prev, max_select: Number(e.target.value || 1) }))}/>
                    <Input type="number" placeholder="정렬" value={newGroupDraft.display_order} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewGroupDraft((prev) => ({ ...prev, display_order: Number(e.target.value || 0) }))}/>
                    <select className="form-select" value={newGroupDraft.is_required ? "true" : "false"} onChange={(e) => setNewGroupDraft((prev) => ({ ...prev, is_required: e.target.value === "true" }))}>
                      <option value="false">선택</option>
                      <option value="true">필수</option>
                    </select>
                    <Button color="soft-primary" onClick={handleCreateGroup}>그룹 추가</Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {groups.map((group) => {
                const groupDraft = getGroupEdit(group);
                const groupOptions = optionsByGroup[group.id] || [];
                const newOptionDraft = newOptionDrafts[group.id] || { name: "", extra_price: 0, tax_amount: 0, supply_amount: 0, tax_free_amount: 0, tip_amount: 0, display_order: 0, is_active: true };
                return (<div key={group.id} className="border rounded p-3">
                        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end mb-2">
                          <div className="md:col-span-2">
                            <Label className="form-label">그룹명</Label>
                            <Input value={groupDraft.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupEdits((prev) => ({ ...prev, [group.id]: { ...groupDraft, name: e.target.value } }))}/>
                          </div>
                          <div>
                            <Label className="form-label">최소</Label>
                            <Input type="number" value={groupDraft.min_select} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupEdits((prev) => ({ ...prev, [group.id]: { ...groupDraft, min_select: Number(e.target.value || 0) } }))}/>
                          </div>
                          <div>
                            <Label className="form-label">최대</Label>
                            <Input type="number" value={groupDraft.max_select} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupEdits((prev) => ({ ...prev, [group.id]: { ...groupDraft, max_select: Number(e.target.value || 1) } }))}/>
                          </div>
                          <div>
                            <Label className="form-label">정렬</Label>
                            <Input type="number" value={groupDraft.display_order} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGroupEdits((prev) => ({ ...prev, [group.id]: { ...groupDraft, display_order: Number(e.target.value || 0) } }))}/>
                          </div>
                          <div>
                            <Label className="form-label">필수 여부</Label>
                            <select className="form-select" value={groupDraft.is_required ? "true" : "false"} onChange={(e) => setGroupEdits((prev) => ({ ...prev, [group.id]: { ...groupDraft, is_required: e.target.value === "true" } }))}>
                              <option value="false">선택</option>
                              <option value="true">필수</option>
                            </select>
                          </div>
                          <div className="d-flex gap-2">
                            <Button color="soft-primary" size="sm" onClick={() => handleSaveGroup(group)}>저장</Button>
                            <Button color="soft-danger" size="sm" onClick={() => handleDeleteGroup(group)}>삭제</Button>
                          </div>
                        </div>

                        <div className="border rounded p-2 mb-2 bg-light">
                          <div className="grid grid-cols-1 md:grid-cols-10 gap-2 items-end">
                            <div className="md:col-span-2">
                              <Label className="form-label">옵션명</Label>
                              <Input value={newOptionDraft.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, name: e.target.value } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">추가 금액</Label>
                              <Input type="number" value={newOptionDraft.extra_price} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, extra_price: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">공급가</Label>
                              <Input type="number" value={newOptionDraft.supply_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, supply_amount: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">세액</Label>
                              <Input type="number" value={newOptionDraft.tax_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, tax_amount: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">비과세</Label>
                              <Input type="number" value={newOptionDraft.tax_free_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, tax_free_amount: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">팁</Label>
                              <Input type="number" value={newOptionDraft.tip_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, tip_amount: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">정렬</Label>
                              <Input type="number" value={newOptionDraft.display_order} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, display_order: Number(e.target.value || 0) } }))}/>
                            </div>
                            <div>
                              <Label className="form-label">상태</Label>
                              <select className="form-select" value={newOptionDraft.is_active ? "true" : "false"} onChange={(e) => setNewOptionDrafts((prev) => ({ ...prev, [group.id]: { ...newOptionDraft, is_active: e.target.value === "true" } }))}>
                                <option value="true">활성</option>
                                <option value="false">비활성</option>
                              </select>
                            </div>
                            <Button color="soft-primary" onClick={() => handleCreateOption(group.id)}>옵션 추가</Button>
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-sm align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>ID</th>
                                <th>옵션명</th>
                                <th>추가금액</th>
                                <th>공급가</th>
                                <th>세액</th>
                                <th>비과세</th>
                                <th>팁</th>
                                <th>정렬</th>
                                <th>상태</th>
                                <th>관리</th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupOptions.map((option) => {
                        const optionDraft = getOptionEdit(option);
                        return (<tr key={option.id}>
                                    <td>{option.id}</td>
                                    <td>
                                      <Input value={optionDraft.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, name: e.target.value } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.extra_price} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, extra_price: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.supply_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, supply_amount: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.tax_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, tax_amount: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.tax_free_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, tax_free_amount: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.tip_amount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, tip_amount: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <Input type="number" value={optionDraft.display_order} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, display_order: Number(e.target.value || 0) } }))}/>
                                    </td>
                                    <td>
                                      <select className="form-select" value={optionDraft.is_active ? "true" : "false"} onChange={(e) => setOptionEdits((prev) => ({ ...prev, [option.id]: { ...optionDraft, is_active: e.target.value === "true" } }))}>
                                        <option value="true">활성</option>
                                        <option value="false">비활성</option>
                                      </select>
                                    </td>
                                    <td>
                                      <Button color="soft-primary" size="sm" className="me-1" onClick={() => handleSaveOption(option)}>저장</Button>
                                      <Button color="soft-danger" size="sm" onClick={() => handleDeleteOption(option)}>삭제</Button>
                                    </td>
                                  </tr>);
                    })}
                              {groupOptions.length === 0 && (<tr>
                                  <td colSpan={10} className="text-center text-muted py-3">옵션이 없습니다.</td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>);
            })}
                  {groups.length === 0 && <p className="text-muted mb-0">옵션 그룹이 없습니다.</p>}
                </div>
              </>)}
          </CardBody>
        </Card>
      </Container>
    </div>);
};
export default MenuForm;
